import { useState, useEffect, useCallback, useMemo, type JSX } from 'react';
import type { ButtonProps } from '@fastnorth/kraken/components/button';
import { Sheet, SheetClose } from '@fastnorth/kraken/components/sheet';
import { Div, Flex, List } from '@fastnorth/kraken/components/layout';
import { Paragraph, Text } from '@fastnorth/kraken/components/typography';
import { useTranslation } from 'react-i18next';
import { NavLink } from '@fastnorth/kraken/components/navigation';
import Badge from '@fastnorth/kraken/components/badge';
import { SearchCTA } from '@fastnorth/kraken/components/input';
import { useLazyQuery } from '@apollo/client';
import {
  FIND_PARTICIPANTS,
  type FindParticipantsData,
  type FindParticipantsVariables,
} from './graphql/findParticipants';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import type { KarwaUrlParams } from '../../views/types';
import AdButlerBlock from '../ad-butler-block/AdButlerBlock';
import { useApp } from '../../hooks/AppProvider';
import { useFollowing } from '../../hooks/FollowingProvider';
import getEnvFromHostname from '../../utils/getEnvFromHostname';
import { getAllFollowsForEvent } from '../../views/following/utils/getAllFollowsForEvent';
import PushNotificationsDialog from '../push-notifications-dialog/PushNotificationsDialog';
import hasActiveSubscription from '../../lib/pwa/hasActiveSubscription';
import isPushSupported from '../../lib/pwa/isPushSupported';
import areNotificationsDenied from '../../lib/pwa/areNotificationsDenied';
import { MapPin, Search } from 'lucide-react';
import { Button } from '@fastnorth/kraken/components/button';
import SearchResultsSummary from './components/SearchResultsSummary.tsx';

const ParticipantSearch = (): JSX.Element => {
  const { eventUniqueCode } = useParams<KarwaUrlParams>();
  const [
    { event, hasRacesWithPredictiveTracking, hasUnsearchableResults, participantSearchSheetState },
    dispatch,
  ] = useApp();
  const { t } = useTranslation('translation', {
    keyPrefix: 'karwa.components.search_results',
  });
  const [phrase, setPhrase] = useState<string>('');
  const [isPushNotificationsDialogOpen, setIsPushNotificationsDialogOpen] = useState(false);
  const [{ following }] = useFollowing();
  const env = getEnvFromHostname();
  const followsForEvent = getAllFollowsForEvent(following, event);
  const displayFollowBadge = followsForEvent.length > 0;
  const [searchParams, setSearchParams] = useSearchParams();
  const { search } = useLocation();

  const [getData, { loading, error, data, called }] = useLazyQuery<
    FindParticipantsData,
    FindParticipantsVariables
  >(FIND_PARTICIPANTS, {
    variables: {
      eventUniqueCode,
      phrase,
    },
    fetchPolicy: 'no-cache',
    context: {
      timeout: 10000, // 10 second timeout
    },
  });

  const eid = event?.eid;
  const { findParticipants: searchResults } = data || {};
  const results = phrase ? searchResults : undefined;

  const helperText = useMemo(() => {
    if (loading) {
      return t('still_searching');
    } else {
      if (results?.total || results?.total === 0) {
        return t('search_results', {
          searchResultsCount: results.total,
          searchQuery: results.query,
        });
      }
    }
    return null;
  }, [results, loading, t]);

  const handleClose = useCallback(
    (isOpen: boolean) => {
      setPhrase('');
      dispatch({ type: 'setSearchSheetState', payload: isOpen });
    },
    [dispatch]
  );

  const { exact, other } = results || { exact: [], other: [] };
  const allResults = [...exact, ...other];

  const trigger: ButtonProps = useMemo(() => {
    return {
      variant: 'default',
      size: 'lg',
      children: (
        <>
          <Search size={16} strokeWidth={2} className={'mr-5'} />
          {t('find_a_participant')}
        </>
      ),
    };
  }, [t]);

  const handlePushNotificationsDialog = async (): Promise<void> => {
    // we only want to show the dialog if the user has not granted or denied notifications
    if (
      isPushSupported() &&
      !(await hasActiveSubscription()) &&
      !(await areNotificationsDenied())
    ) {
      setIsPushNotificationsDialogOpen(true);
    }
  };

  useEffect(() => {
    if (!eventUniqueCode || phrase.length < 1) return;
    getData({
      fetchPolicy: 'no-cache',
      variables: {
        eventUniqueCode,
        phrase,
      },
    });
  }, [phrase, getData, eventUniqueCode]);

  const [firstSubEvent] = event?.subEvents || [];

  const displayAdBlock = !!(eid && !error);

  const leaderboardUrl = `/events/${eventUniqueCode}/race/${firstSubEvent?.id || ''}${search}`;
  const followingUrl = `/events/${eventUniqueCode}/following${search}`;
  const hasSearched = !!phrase && called && !loading;

  return (
    <Sheet
      id={'participant-search'}
      onOpenChange={handleClose}
      open={participantSearchSheetState}
      width={'md'}
      trigger={trigger}
      sheetTitle={{ text: t('find_a_participant') }}
      headerContent={
        <>
          <PushNotificationsDialog
            open={isPushNotificationsDialogOpen}
            onOpenChange={setIsPushNotificationsDialogOpen}
          />
          <Div spacing={'2 0 0'}>
            <SearchCTA
              id={'participant-search'}
              debounceMillis={700}
              label={{
                children: t('try_name_or_bib', {
                  name: <Text fontWeight={'semibold'}>{t('name')}</Text>,
                  bib: <Text fontWeight={'semibold'}>{t('bib')}</Text>,
                }),
              }}
              clearText={t('clear_button_label')}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={true}
              onSearch={(value: string) => {
                setPhrase(value);
              }}
            />
          </Div>
        </>
      }>
      <Flex
        gap={'5'}
        className={'flex-col h-full bg-gray-100'}
        onClick={allResults.length > 0 ? handlePushNotificationsDialog : undefined}>
        {helperText && (
          <Div className={'px-5 py-3'}>
            <Text>{helperText}</Text>
          </Div>
        )}
        {hasSearched && !!results && <SearchResultsSummary results={results} />}

        <Div className={'px-5 mt-auto'}>
          {error && (
            <>
              <Paragraph>{t('search_not_working')}</Paragraph>
              <Paragraph>{t('try_again_or_visit_leaderboards')}</Paragraph>
            </>
          )}
          {hasUnsearchableResults && (
            <>
              <Paragraph>
                {t('unsearchable_results', {
                  hasStructuredResults: results?.total ? 1 : 0,
                })}
              </Paragraph>
              <Paragraph>{t('please_review_the_leaderboards')}</Paragraph>
            </>
          )}

          <Flex spacing={'5 0'} className={'flex-wrap justify-end'} gap={'3'}>
            {env === 'local' && (
              <Div className={'ml-[100%] my-4'}>
                <Button
                  onClick={(evt) => {
                    evt.preventDefault();
                    searchParams.delete('follows');
                    searchParams.delete('active');
                    setSearchParams(searchParams);
                  }}
                  variant={'outline'}>
                  {t('clear_all_followers')}
                </Button>
              </Div>
            )}

            {firstSubEvent && (
              <SheetClose>
                <NavLink id={'leaderboard-link'} accent={'secondary'} variant={'pill'}>
                  <Link to={leaderboardUrl}>
                    <List size={16} strokeWidth={2} className={'mr-3'} />
                    {t('leaderboards')}
                  </Link>
                </NavLink>
              </SheetClose>
            )}

            {hasRacesWithPredictiveTracking && (
              <SheetClose>
                <NavLink id={'following-link'} accent={'secondary'} variant={'pill'}>
                  <Link to={followingUrl}>
                    <MapPin size={16} strokeWidth={2} className={'mr-3'} />
                    {t('following')}
                    {displayFollowBadge && (
                      <Badge
                        data-testid={'badge-following-count'}
                        variant={'secondary'}
                        spacing={'0 0 0 4'}>
                        {followsForEvent.length}
                      </Badge>
                    )}
                  </Link>
                </NavLink>
              </SheetClose>
            )}
          </Flex>
        </Div>

        {displayAdBlock && (
          <AdButlerBlock
            eventId={eid}
            className={'mb-5 flex w-full justify-center'}
            url={
              'https://servedbyadbutler.com/adserve/;ID=168344;size=320x50;setID=833020;type=json'
            }
          />
        )}
      </Flex>
    </Sheet>
  );
};

export default ParticipantSearch;
